import React from 'react';
import { cn } from '@/lib/utils';

interface LoadingDotsProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  color?: 'default' | 'muted' | 'primary';
}

export const LoadingDots: React.FC<LoadingDotsProps> = ({ 
  className, 
  size = 'md', 
  color = 'default' 
}) => {
  const sizeClasses = {
    sm: 'w-1 h-1',
    md: 'w-1.5 h-1.5',
    lg: 'w-2 h-2'
  };

  const colorClasses = {
    default: 'bg-foreground',
    muted: 'bg-muted-foreground',
    primary: 'bg-primary'
  };

  return (
    <div className={cn("flex items-center space-x-1", className)}>
      <div 
        className={cn(
          "rounded-full animate-pulse",
          sizeClasses[size],
          colorClasses[color]
        )}
        style={{
          animationDelay: '0ms',
          animationDuration: '1.4s'
        }}
      />
      <div 
        className={cn(
          "rounded-full animate-pulse",
          sizeClasses[size],
          colorClasses[color]
        )}
        style={{
          animationDelay: '160ms',
          animationDuration: '1.4s'
        }}
      />
      <div 
        className={cn(
          "rounded-full animate-pulse",
          sizeClasses[size],
          colorClasses[color]
        )}
        style={{
          animationDelay: '320ms',
          animationDuration: '1.4s'
        }}
      />
    </div>
  );
};

// Alternative implementation with CSS keyframes for smoother animation
export const LoadingDotsSmooth: React.FC<LoadingDotsProps> = ({ 
  className, 
  size = 'md', 
  color = 'default' 
}) => {
  const sizeClasses = {
    sm: 'w-1 h-1',
    md: 'w-1.5 h-1.5',
    lg: 'w-2 h-2'
  };

  const colorClasses = {
    default: 'bg-foreground',
    muted: 'bg-muted-foreground',
    primary: 'bg-primary'
  };

  return (
    <div className={cn("flex items-center space-x-1", className)}>
      <div 
        className={cn(
          "rounded-full",
          sizeClasses[size],
          colorClasses[color]
        )}
        style={{
          animation: 'loadingDots 1.4s infinite ease-in-out both',
          animationDelay: '0ms'
        }}
      />
      <div 
        className={cn(
          "rounded-full",
          sizeClasses[size],
          colorClasses[color]
        )}
        style={{
          animation: 'loadingDots 1.4s infinite ease-in-out both',
          animationDelay: '160ms'
        }}
      />
      <div 
        className={cn(
          "rounded-full",
          sizeClasses[size],
          colorClasses[color]
        )}
        style={{
          animation: 'loadingDots 1.4s infinite ease-in-out both',
          animationDelay: '320ms'
        }}
      />
      <style>{`
        @keyframes loadingDots {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};
